import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DataTable, Th, Td, Tr, useToast, useConfirm, Button, documentTitle, navLabel, useDocumentTitle } from '@region13/ui'
import type { Subfield, SubfieldInput, LayoutMatchContext } from '@region13/shared-types'
import { LoadFailure } from '../components/LoadFailure'
import { SubfieldDialog } from '../components/SubfieldDialog'
import { api } from '../lib/api'
import { FIELDS_NAV } from '../lib/nav'
import { Checkbox } from '../components/ui/Checkbox'

export const Route = createFileRoute('/layouts/$layoutId')({ component: LayoutDetail })

function LayoutDetail() {
  const { layoutId } = Route.useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const { confirm } = useConfirm()
  const [dialog, setDialog] = useState<{ open: boolean; editing?: Subfield }>({ open: false })

  const data = useQuery({ queryKey: ['subfields', layoutId], queryFn: () => api.layoutSubfields(layoutId) })
  // A child page heads from its data, so the tab title waits for the layout.
  useDocumentTitle(documentTitle(FIELDS_NAV, data.data?.layout.name ?? null))
  // Subfield CRUD changes `subfield_count` on the venue list, and the layout
  // active-toggle changes the venue's inactive-label — refresh both alongside
  // this layout's own subfields.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['subfields', layoutId] })
    qc.invalidateQueries({ queryKey: ['venues'] })
    const venueSlug = data.data?.layout.venue_slug
    if (venueSlug) qc.invalidateQueries({ queryKey: ['venue', venueSlug] })
  }

  const layoutActiveM = useMutation({
    mutationFn: (active: boolean) => api.updateLayout(layoutId, { active }),
    onSuccess: () => { invalidate(); toast.success('Layout updated') },
    onError: (e) => toast.error((e as Error).message),
  })
  const createM = useMutation({
    mutationFn: (input: SubfieldInput) => api.createSubfield(layoutId, input),
    onSuccess: () => { invalidate(); setDialog({ open: false }); toast.success('Subfield created') },
    onError: (e) => toast.error((e as Error).message),
  })
  const updateM = useMutation({
    mutationFn: (v: { id: string; input: SubfieldInput }) => api.updateSubfield(v.id, v.input),
    onSuccess: () => { invalidate(); setDialog({ open: false }); toast.success('Subfield saved') },
    onError: (e) => toast.error((e as Error).message),
  })
  // Announces itself, like every other mutation on this page. It was silent from
  // PR #10 until 2026-07-30 — the row graying was the only feedback, which is
  // thin for the toggle that decides whether a field can be scheduled on at all.
  // Carries the label so the toast says which subfield, since a long table makes
  // "Subfield updated" ambiguous.
  const activeM = useMutation({
    mutationFn: (v: { id: string; active: boolean; label: string }) =>
      api.updateSubfield(v.id, { active: v.active }),
    onSuccess: (_res, v) => {
      invalidate()
      toast.success(`${v.label} is now ${v.active ? 'active' : 'inactive'}`)
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteSubfield(id),
    onSuccess: () => { invalidate(); toast.success('Subfield deleted') },
    onError: (e) => toast.error((e as Error).message),
  })
  const matchM = useMutation({
    mutationFn: (v: { id: string; uid: string | null }) => api.matchSubfield(v.id, v.uid),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  })

  async function onDelete(s: Subfield) {
    const ok = await confirm({ title: `Delete "${s.label}"?`, confirmLabel: 'Delete', tone: 'danger' })
    if (!ok) return
    deleteM.mutate(s.id)
  }

  if (data.isLoading) return <p role="status" className="py-16 text-center text-sm text-ink-soft">Loading…</p>
  if (data.isError || !data.data) return <LoadFailure error={data.error} noun="layout" />

  const { layout, subfields, match } = data.data
  const venueArchived = data.data.venue_retired
  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Breadcrumb" className="text-xs text-ink-soft">
        <Link to="/" activeOptions={{ exact: true }} className="hover:underline">{navLabel(FIELDS_NAV, '/')}</Link> &rsaquo;{' '}
        <Link to="/venues" activeOptions={{ exact: true }} className="hover:underline">{navLabel(FIELDS_NAV, '/venues')}</Link> &rsaquo;{' '}
        <Link to="/venues/$slug" params={{ slug: layout.venue_slug }} activeOptions={{ exact: true }} className="hover:underline">
          {data.data.venue_title ?? layout.venue_slug}
        </Link> &rsaquo; <span aria-current="page">{layout.name}</span>
      </nav>
      {venueArchived && (
        <p className="rounded-lg border border-brand-gold/40 bg-brand-gold/10 px-3 py-2 text-xs text-ink">
          This venue is archived, so its fields are switched off. Restore it on the{' '}
          <Link to="/venues/$slug" params={{ slug: layout.venue_slug }} className="font-semibold hover:underline">
            venue page
          </Link>{' '}
          to turn them back on.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-extrabold text-ink">{layout.name} <span className="text-sm font-normal text-ink-soft">({layout.purpose})</span></h1>
          <label className="flex items-center gap-1 text-sm text-ink-soft">
            <Checkbox aria-label="Active" checked={layout.active} disabled={venueArchived}
              onChange={(e) => layoutActiveM.mutate(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="flex items-center gap-2">
          {/* The map editor is the vanilla-JS app served by the worker at /map,
              outside this SPA's router, so this is a plain anchor rather than a
              <Link>. Preselect this layout's venue and variant so it opens
              straight into the right map instead of the picker. */}
          <a
            href={`/map?field=${encodeURIComponent(layout.venue_slug)}&layout=${encodeURIComponent(layout.purpose)}`}
            className="btn-secondary"
          >
            Edit map
          </a>
          <Button variant="primary" onClick={() => setDialog({ open: true })}>New subfield</Button>
        </div>
      </div>
      {match?.scope === 'fallback' && (
        <p className="text-xs text-ink-soft">
          No InLeague fields are matched to this venue yet — showing all unlinked fields. Match the venue in the scheduler admin to narrow this list.
        </p>
      )}
      {subfields.length === 0 ? (
        <p className="text-sm text-ink-soft">No subfields yet.</p>
      ) : (
        <DataTable label={`Subfields for ${layout.name}`}>
          <thead><Tr>
            <Th>Label</Th><Th>Division</Th><Th>Goal</Th><Th>Quality</Th><Th>Lights</Th>
            {match && <Th>InLeague</Th>}
            <Th>Active</Th><Th></Th>
          </Tr></thead>
          <tbody>
            {subfields.map((s) => (
              <Tr key={s.id} muted={!s.active}>
                <Td>{s.label}</Td>
                <Td>{s.division ?? 'any'}</Td>
                <Td>{s.goal_size ?? ''}</Td>
                <Td>{s.quality}</Td>
                <Td>{s.lights ? 'yes' : ''}</Td>
                {match && (
                  <Td>
                    <MatchCell subfield={s} match={match} onPick={(uid) => matchM.mutate({ id: s.id, uid })} />
                  </Td>
                )}
                <Td>
                  <Checkbox aria-label={`Active ${s.label}`} checked={s.active} disabled={venueArchived}
                    onChange={(e) => activeM.mutate({ id: s.id, active: e.target.checked, label: s.label })} />
                </Td>
                <Td>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setDialog({ open: true, editing: s })}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => void onDelete(s)}>Delete</Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {dialog.open && (
        <SubfieldDialog
          open
          initial={dialog.editing}
          busy={createM.isPending || updateM.isPending}
          onClose={() => setDialog({ open: false })}
          onSave={(input) => (dialog.editing ? updateM.mutate({ id: dialog.editing.id, input }) : createM.mutate(input))}
        />
      )}
    </div>
  )
}

function MatchCell({ subfield, match, onPick }: {
  subfield: Subfield
  match: LayoutMatchContext
  onPick: (uid: string | null) => void
}) {
  const suggested = match.suggestions[subfield.id] ?? null
  const suggestedName = suggested ? match.candidates.find((c) => c.uid === suggested)?.name ?? suggested : null
  return (
    <select
      className="input min-w-[9rem] max-w-[12rem]"
      aria-label={`InLeague field for ${subfield.label}`}
      value={subfield.inleague_field_uid ?? ''}
      onChange={(e) => onPick(e.target.value || null)}
    >
      <option value="">{suggestedName ? `— (suggested: ${suggestedName})` : '— unmatched'}</option>
      {match.candidates.map((c) => (
        <option
          key={c.uid}
          value={c.uid}
          disabled={c.linked_here && c.uid !== subfield.inleague_field_uid}
        >
          {c.name ?? c.uid}{c.linked_elsewhere ? ` (also: ${c.linked_elsewhere})` : ''}
        </option>
      ))}
    </select>
  )
}
